// Admin RBAC editor. Toggling a cell PUTs an override; effect is 'default' when the new
// state matches the role's hardcoded default (so we only persist real deltas). This is
// the client half of F5 — it gates off the same permissions the server routes do.
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import type { RbacMatrix as Matrix } from '@alignova/shared';
import { api, isDeferred } from '../../lib/api.ts';
import { useLiveEvent } from '../../lib/useLive.ts';
import { t } from '../../i18n/index.ts';
import { Card, PageHeader } from '../../ui/primitives.tsx';

type Effect = 'grant' | 'revoke' | 'default';

/** Apply an override to a matrix client-side, mirroring the server's resolution, so a
 *  toggle shows instantly (and stays correct while a mutation is queued offline). */
function withOverride(m: Matrix, role: string, key: string, effect: Effect): Matrix {
  const overrides = m.overrides.filter((o) => !(o.role === role && o.permissionKey === key));
  if (effect !== 'default') overrides.push({ role, permissionKey: key, effect });
  const base = new Set(role === 'super_admin' ? m.permissions.map((p) => p.key) : m.defaults[role] ?? []);
  for (const o of overrides) {
    if (o.role !== role) continue;
    if (o.effect === 'grant') base.add(o.permissionKey);
    else base.delete(o.permissionKey);
  }
  return { ...m, overrides, effective: { ...m.effective, [role]: [...base] } };
}

export function RbacMatrix() {
  const [matrix, setMatrix] = useState<Matrix | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savingCell, setSavingCell] = useState<string | null>(null);
  const [queued, setQueued] = useState(false);

  const reload = useCallback(() => {
    api
      .get<Matrix>('/rbac/matrix')
      .then(setMatrix)
      .catch(() => setError('Failed to load matrix'));
  }, []);

  useEffect(reload, [reload]);

  // Another admin (or our own replayed offline edit) changed an override — pull fresh
  // truth. This is the "an SSE delta reaches a second tab" half of F8.
  useLiveEvent('rbac.changed', () => {
    setQueued(false);
    reload();
  });

  const grouped = useMemo(() => {
    if (!matrix) return [];
    const byGroup = new Map<string, Matrix['permissions']>();
    for (const p of matrix.permissions) {
      const list = byGroup.get(p.group) ?? [];
      list.push(p);
      byGroup.set(p.group, list);
    }
    return [...byGroup.entries()];
  }, [matrix]);

  if (!matrix) {
    return error ? (
      <p className="text-status-urgent-fg">{error}</p>
    ) : (
      <p className="text-muted">{t('common.loading')}</p>
    );
  }

  const effectiveHas = (role: string, key: string) => matrix.effective[role]?.includes(key) ?? false;
  const defaultHas = (role: string, key: string) =>
    role === 'super_admin' || (matrix.defaults[role]?.includes(key) ?? false);
  const isOverridden = (role: string, key: string) =>
    matrix.overrides.some((o) => o.role === role && o.permissionKey === key);

  async function toggle(role: string, key: string) {
    if (role === 'super_admin' || !matrix) return; // super_admin always has all
    const desired = !effectiveHas(role, key);
    const def = defaultHas(role, key);
    const effect: Effect = desired === def ? 'default' : desired ? 'grant' : 'revoke';
    const cell = `${role}:${key}`;
    const prev = matrix;
    // Optimistic: reflect the change immediately; the server response (or the queued
    // replay's rbac.changed event) is authoritative and reconciles later.
    setMatrix(withOverride(matrix, role, key, effect));
    setSavingCell(cell);
    setError(null);
    try {
      const res = await api.put<Matrix>('/rbac/override', { role, permissionKey: key, effect }, { queue: true });
      if (isDeferred(res)) setQueued(true);
      else setMatrix(res);
    } catch {
      setMatrix(prev); // real rejection - roll the optimistic edit back
      setError('Update failed');
    } finally {
      setSavingCell(null);
    }
  }

  return (
    <div>
      <PageHeader title={t('rbac.title')} subtitle={t('rbac.subtitle')} />
      {queued && (
        <div className="mb-3 rounded border border-line bg-surface-2 px-3 py-2 text-xs text-muted">
          {t('sync.queued')}
        </div>
      )}
      {error && <p className="mb-3 text-xs text-status-urgent-fg">{error}</p>}
      <Card className="overflow-x-auto p-0">
        <table className="table-grid w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-line">
              <th className="sticky left-0 bg-surface px-4 py-3 text-left font-medium">Permission</th>
              {matrix.roles.map((r) => (
                <th key={r.key} className="px-3 py-3 text-center font-mono text-xs" title={r.label}>
                  {r.key.replace('_', ' ')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grouped.map(([group, perms]) => (
              <Fragment key={group}>
                <tr className="bg-surface-2">
                  <td colSpan={matrix.roles.length + 1} className="px-4 py-1.5 font-mono text-xs uppercase tracking-widest text-muted">
                    {group}
                  </td>
                </tr>
                {perms.map((p) => (
                  <tr key={p.key} className="border-b border-line hover:bg-surface-2">
                    <td className="sticky left-0 bg-surface px-4 py-2" title={p.description}>
                      <span className="font-mono text-xs">{p.key}</span>
                    </td>
                    {matrix.roles.map((r) => {
                      const on = effectiveHas(r.key, p.key);
                      const overridden = isOverridden(r.key, p.key);
                      const cell = `${r.key}:${p.key}`;
                      return (
                        <td key={r.key} className="px-3 py-2 text-center">
                          <button
                            onClick={() => toggle(r.key, p.key)}
                            disabled={r.key === 'super_admin' || savingCell === cell}
                            title={overridden ? 'Overridden' : 'Default'}
                            className={`h-5 w-5 rounded border transition ${
                              on ? 'border-accent bg-accent' : 'border-line bg-surface'
                            } ${overridden ? 'ring-2 ring-aqua ring-offset-1' : ''} ${
                              r.key === 'super_admin' ? 'opacity-60' : 'hover:opacity-80'
                            }`}
                            aria-label={`${r.key} ${p.key} ${on ? 'granted' : 'denied'}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </Card>
      <p className="mt-3 text-xs text-muted">
        Cells with an aqua ring are overrides on top of the role default. Super-admin always has every permission.
      </p>
    </div>
  );
}
